import { useEffect, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import {
  Select, SelectItem, Tab, TabGroup, TabList, TabPanel, TabPanels,
} from '@tremor/react';
import { currentUser } from '../api/auth';
import { fetchCustomer, CustomerNotFoundError } from '../api/customer';
import { fetchOverview } from '../api/client';
import AccessDenied from '../components/AccessDenied';
import EmptyState from '../components/EmptyState';
import ErrorRetry from '../components/ErrorRetry';
import TableSkeleton from '../components/TableSkeleton';
import type { CustomerBundle, OverviewPayload, User } from '../api/types';
import CustomerHeader from '../components/CustomerHeader';
import CustomerScorecard from '../components/CustomerScorecard';
import CustomerArSection from '../components/CustomerArSection';
import CustomerActivitySection from '../components/CustomerActivitySection';
import AccountStatement from '../components/AccountStatement';
import TicketDetailModal from '../components/TicketDetailModal';
import Header from '../components/Header';
import { t } from '../lib/i18n';

export default function Customer() {
  const { anum } = useParams<{ anum: string }>();
  const anumNum = Number(anum);
  // Tri-state currentUser load: undefined = pending; null = redirect; User = render.
  const [user, setUser] = useState<User | null | undefined>(undefined);
  const [bundle, setBundle] = useState<CustomerBundle | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [ticketOpen, setTicketOpen] = useState<{ sid: number; tnum: number } | null>(null);
  // Stores list + chosen store for the Estado de cuenta tab (AR is per-store).
  const [overview, setOverview] = useState<OverviewPayload | null>(null);
  const [stmtSid, setStmtSid] = useState<number>(0);

  useEffect(() => {
    let alive = true;
    currentUser()
      .then((u) => { if (alive) setUser(u); })
      .catch(() => { if (alive) setUser(null); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!user) return;
    let alive = true;
    fetchOverview(user.role)
      .then((p) => {
        if (!alive) return;
        setOverview(p);
        setStmtSid((cur) => (cur > 0 ? cur : (user.sid_assigned ?? (p.stores[0]?.sid ?? 1))));
      })
      .catch(() => { if (alive) setStmtSid((cur) => (cur > 0 ? cur : (user.sid_assigned ?? 1))); });
    return () => { alive = false; };
  }, [user]);

  const load = () => {
    if (!user || !Number.isFinite(anumNum)) return;
    let alive = true;
    setErr(null);
    setNotFound(false);
    setBundle(null);
    fetchCustomer(anumNum)
      .then((b) => { if (alive) setBundle(b); })
      .catch((e: unknown) => {
        if (!alive) return;
        if (e instanceof CustomerNotFoundError) setNotFound(true);
        else setErr(e instanceof Error ? e.message : String(e));
      });
    return () => { alive = false; };
  };
  useEffect(() => load(), [user, anumNum]);

  if (user === undefined) return null;
  if (user === null) return <Navigate to="/login" replace />;

  const isAuthorized = user.role === 'owner' || user.role === 'manager' || user.role === 'accounting';
  if (!isAuthorized) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header user={user} />
        <main className="flex-1 p-4"><AccessDenied user={user} /></main>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header user={user} />
        <main className="flex-1 p-4 space-y-4">
          <EmptyState
            message={t.customer.notFound}
            action={<Link to="/ar" className="text-tremor-brand hover:underline">{t.customer.back}</Link>}
          />
        </main>
      </div>
    );
  }

  if (err) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header user={user} />
        <main className="flex-1 p-4"><ErrorRetry message={err} onRetry={load} /></main>
      </div>
    );
  }

  if (!bundle) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header user={user} />
        <main className="flex-1 p-4 space-y-4"><TableSkeleton rows={8} cols={4} /></main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header user={user} />
      <main className="flex-1 p-4 space-y-4 max-w-screen-2xl mx-auto w-full">
        <CustomerHeader header={bundle.header} />
        <TabGroup>
          <TabList variant="solid">
            <Tab>{t.customer.tabSummary}</Tab>
            <Tab>{t.statement.tab}</Tab>
          </TabList>
          <TabPanels>
            <TabPanel className="space-y-4 mt-4">
              <CustomerScorecard anum={bundle.header.anum} />
              <CustomerArSection
                ar={bundle.ar}
                openInvoices={bundle.open_invoices}
                recentPayments={bundle.recent_payments}
                dsoDays={bundle.dso_days}
              />
              <CustomerActivitySection
                recentTickets={bundle.recent_tickets}
                topParts={bundle.top_parts}
                revenueTrend={bundle.revenue_trend}
                onTicketClick={(sid, tnum) => setTicketOpen({ sid, tnum })}
              />
            </TabPanel>
            <TabPanel className="space-y-4 mt-4">
              {overview && overview.stores.length > 1 && (
                <div className="w-full md:w-56">
                  <div className="text-tremor-content text-xs mb-1">{t.statement.store}</div>
                  <Select value={stmtSid > 0 ? String(stmtSid) : ''} onValueChange={(v) => setStmtSid(Number(v))} enableClear={false}>
                    {overview.stores.map((s) => (
                      <SelectItem key={s.sid} value={String(s.sid)}>{s.name}</SelectItem>
                    ))}
                  </Select>
                </div>
              )}
              {stmtSid > 0 && <AccountStatement sid={stmtSid} anum={bundle.header.anum} />}
            </TabPanel>
          </TabPanels>
        </TabGroup>
      </main>
      {ticketOpen && (
        <TicketDetailModal
          sid={ticketOpen.sid}
          tnum={ticketOpen.tnum}
          canDownloadInvoice={user.role !== 'staff'}
          onClose={() => setTicketOpen(null)}
        />
      )}
    </div>
  );
}
